import { Dispatch, useContext, useEffect, useMemo, useState } from 'react';
import { KTCard, KTSVG, KTCardBody } from '../../../_metronic/helpers';
import { IHotelQueryParams } from '../type';
import { getHotelColumns, getItineraryColumns } from './hotelColumns';
import OATableComponent, { OATablePaginationProps } from '../../../components/OATableComponent';
import { GridEventListener } from '@mui/x-data-grid-pro';
import FlightContext from '../context/HotelContext';
import { useNavigate } from 'react-router-dom';
import OrderFilterNavItem from '../../../components/OAFilterNav';
import { flightFilterNavs } from '../../order-management/constants';

interface IHotelsHistory {
  list: any;
  totalPage: number;
  queryParams: IHotelQueryParams;
  setQueryParams: Dispatch<React.SetStateAction<IHotelQueryParams>>;
  onChangeRangePicker: (range: any) => void;
  loading: boolean;
}

export const HotelOrderHistory = (props: IHotelsHistory) => {
  const navigate = useNavigate();
  const { list, queryParams, setQueryParams, loading } = props;
  const { pageInfo } = useContext(FlightContext)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let ifFound = flightFilterNavs?.find((obj: any) => obj === queryParams?.orderStatus);
    if (!ifFound) {
      setQueryParams((prev: any) => ({
        ...prev,
        orderStatus: '',
      }));
    }
    setSearch(queryParams?.search ?? '')
  }, []);

  const filterNavs = [
    {
      label: 'All',
      value: '',
      count: list?.allTotalOrder,
      show: true
    },
    {
      label: 'Confirmed',
      value: 'CONFIRMED',
      count: list?.totalConfirmedOrder,
      show: true
    },
    {
      label: 'Cancelled',
      value: 'CANCELLED',
      count: list?.totalCancelledOrder,
      show: true
    },
    {
      label: 'Failed',
      value: 'FAILED',
      count: list?.totalFailedOrder,
      show: true
    },
    {
      label: 'Itinerary',
      value: 'ITINERARY',
      count: list?.totalItineraryOrder,
      show: true
    }
  ];

  const tableColumns = useMemo(() => {
    return queryParams?.orderStatus === 'ITINERARY' ? getItineraryColumns() : getHotelColumns();
  }, [queryParams?.orderStatus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.value?.trim()?.length <= 1) {
      updateSearchParams('');
    }
    setSearch(e.target.value.trim());
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key, target } = e;
    const value = (target as HTMLInputElement).value.trim();
  
    if (key === 'Backspace' && value.length <= 1) {
      updateSearchParams('');
    }
  
    if (key === 'Enter') {
      updateSearchParams(value);
    }
  };
  
  const updateSearchParams = (searchValue: string) => {
    setQueryParams(prev => ({
      ...prev,
      search: searchValue,
      pageIndex: 1,
    }));
  };
  
  const handleSearchClick = () => {
    updateSearchParams(search);
  };

  const handleRowClick: GridEventListener<'cellClick'> = params => {
    navigate(`/apps/hotels/${params?.row?.id}`);
  };

  const paginationProps: OATablePaginationProps = {
    pageIndex: queryParams?.pageIndex,
    pageCount: pageInfo?.totalPages,
    total: pageInfo?.totalCount,
    pageSize: pageInfo?.size,
    onPageChange: (value: any) => {
      setQueryParams(prev => ({ ...prev, pageIndex: value }));
    }
  };

  return (
    <KTCard>
      <div className='card-header card-header-stretch border-bottom border-gray-200 min-h-60px'>
        <div className='card-title position-relative search-orders'>
          <KTSVG
            className='position-absolute ms-2 svg-icon-4'
            path='/media/icons/duotune/general/gen021.svg'
          />
          <input
            type='text'
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            value={search}
            className='form-control form-control-sm form-control-solid oa-text-search w-200px ps-9'
            placeholder='Search Orders'
          />
          <div className='d-flex  justify-content-end align-items-center' onClick={handleSearchClick}>
          <KTSVG
            className='position-absolute me-2 svg-icon-4 cursor-pointer'
            path='/media/icons/duotune/arrows/go-search.svg'
          />
          </div>
        </div>
        <div className='card-toolbar m-0 order-search'>
          <ul className='nav nav-stretch nav-line-tabs border-transparent' role='tablist'>
            {filterNavs?.length &&
              filterNavs?.map((obj, idx) =>
                obj?.show ? (
                  <OrderFilterNavItem
                    key={idx}
                    label={obj?.label}
                    orderStatus={queryParams?.orderStatus!}
                    value={obj?.value}
                    count={obj?.count}
                    onClick={() => {
                      setQueryParams(prev => ({
                        ...prev,
                        orderStatus: obj?.value,
                        pageIndex: 1
                      }));
                    }}
                    isLoading={props?.loading}
                  />
                ) : null
              )}
          </ul>
        </div>
      </div>
      <KTCardBody className='px-6 py-5'>
        <OATableComponent
          columns={tableColumns}
          data={list?.content ?? []}
          onCellClick={handleRowClick}
          pagination={paginationProps}
          loading={loading}
        />
      </KTCardBody>
    </KTCard>
  );
};
